import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';

interface EditorClipIdentity {
  id: number;
  skippedClipId: string | null;
  activeClipId: string | null;
}

interface Options {
  initialClipId?: string;
  clips: EditorClipIdentity[];
  selectedClips: number[];
  setSelectedClips: Dispatch<SetStateAction<number[]>>;
  fetchHiddenClip: (options: { variables: { id: string } }) => unknown;
  swiperRef: { current: { slideTo: (index: number) => void } | null };
}

interface SelectionSnapshot {
  urlId: string | null;
  clips: EditorClipIdentity[];
  selection: number[];
  backendId: string | null;
  pendingUrl: string | null;
  requestedUrl: string | null;
  /** The exact state array dispatched by this hook, distinguishing its
   *  acknowledgement from an explicit user click on the same numeric index. */
  expectedSelection: number[] | null;
  writtenUrl: string | null;
}

const backendId = (clip: EditorClipIdentity | undefined) =>
  clip?.skippedClipId || clip?.activeClipId || null;

/** Synchronize directionally: navigation resolves a backend identity, manual
 *  selection updates the URL, and list refreshes preserve that identity. */
export function useEditorClipSelection({
  initialClipId, clips, selectedClips, setSelectedClips,
  fetchHiddenClip, swiperRef,
}: Options) {
  const snapshotRef = useRef<SelectionSnapshot | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const previous = snapshotRef.current;
    const urlId = initialClipId || null;
    const urlChanged = !previous || previous.urlId !== urlId;
    const ownUrlChange = urlChanged && urlId !== null && previous?.writtenUrl === urlId;
    const navigation = urlChanged && !ownUrlChange;
    const selectionChanged = previous && previous.selection !== selectedClips;
    const manualSelection = selectionChanged && previous.expectedSelection !== selectedClips;
    const next: SelectionSnapshot = {
      urlId, clips, selection: selectedClips,
      backendId: previous?.backendId ?? backendId(clips.find((clip) => clip.id === selectedClips[0])),
      pendingUrl: previous?.pendingUrl ?? null,
      requestedUrl: previous?.requestedUrl ?? null,
      expectedSelection: selectionChanged ? null : previous?.expectedSelection ?? null,
      writtenUrl: previous?.writtenUrl ?? null,
    };
    snapshotRef.current = next;

    if (navigation) {
      next.pendingUrl = urlId;
      next.requestedUrl = null;
      next.writtenUrl = null;
    } else if (ownUrlChange) {
      // An acknowledgement of our URL write is not a new navigation request.
      next.writtenUrl = null;
    }

    if (manualSelection && !navigation) {
      // The click came from the previously committed list. A concurrent poll
      // may have reassigned numeric IDs before this effect observes it.
      const clicked = previous.clips.find((clip) => clip.id === selectedClips[0])
        ?? clips.find((clip) => clip.id === selectedClips[0]);
      next.backendId = backendId(clicked);
      next.pendingUrl = null;
      next.requestedUrl = null;
    }

    if (next.pendingUrl) {
      const target = clips.find((clip) => backendId(clip) === next.pendingUrl);
      if (!target) {
        // Keep the requested URL intact until its row exists. Neither a
        // default selection nor a late result for another URL may replace it.
        if (clips.length > 0 && next.requestedUrl !== next.pendingUrl) {
          next.requestedUrl = next.pendingUrl;
          fetchHiddenClip({ variables: { id: next.pendingUrl } });
        }
        return;
      }
      next.backendId = backendId(target);
      next.pendingUrl = null;
    }

    const target = next.backendId
      ? clips.find((clip) => backendId(clip) === next.backendId)
      : clips.find((clip) => clip.id === selectedClips[0]);
    if (!target) return;
    next.backendId = backendId(target);

    const expectedId = next.expectedSelection?.[0] ?? selectedClips[0];
    if (target.id !== expectedId) {
      const selection = [target.id];
      next.expectedSelection = selection;
      setSelectedClips(selection);
      swiperRef.current?.slideTo(clips.indexOf(target));
    }

    if (!next.backendId || searchParams.get('clip') === next.backendId || next.writtenUrl === next.backendId) return;
    next.writtenUrl = next.backendId;
    const params = new URLSearchParams(searchParams);
    params.set('clip', next.backendId);
    setSearchParams(params, { replace: true });
  }, [initialClipId, selectedClips, clips, searchParams, setSearchParams, setSelectedClips, fetchHiddenClip, swiperRef]);
}
